import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

import * as Api from "../../../api";
import Loader from "../../../components/Loader";

const TableOrderingMenuLayoutTab = ({ data, handleChange, sectionName, restaurantData }) => {
  const [loading, setLoading] = useState(true);
  const [menuLayoutList, setMenuLayoutList] = useState([]);
  const selectedLayoutId = data?._idMenuLayout || "";

  useEffect(() => {
    getMenuLayoutsList();
  }, []);

  const getMenuLayoutsList = () => {
    setLoading(true);
    Api.getMenuUiLayoutConfigs().then((response) => {
      if (response.success) {
        const layoutData = response.data?.rows || [];
        setMenuLayoutList(layoutData);
      } else {
        console.error("Failed to fetch menu layouts:", response.msg);
        setMenuLayoutList([]);
      }
      setLoading(false);
    });
  };

  const onSelectLayout = (layoutId) => {
    handleChange(sectionName, "_idMenuLayout", layoutId);
  };

  if (loading) {
    return <Loader height={{ height: 400 }} />;
  }

  return (
    <>
      <Grid item container xs={12} mt={2}>
        <Typography sx={styles.sectionTitle}>
          Table Ordering Menu Layout
        </Typography>
      </Grid>
      
      <Grid item container xs={12} mt={1}>
        <Typography sx={styles.sectionDescription}>
          Select a menu layout for your restaurant's table ordering system
        </Typography>
      </Grid>

      {/* Layout Grid */}
      <Grid container item xs={12} mt={3} spacing={2}>
        {menuLayoutList.length > 0 ? (
          menuLayoutList.map((layout) => {
            const isSelected = layout._id === selectedLayoutId;
            return (
              <Grid
                key={layout._id}
                item
                xs={12}
                sm={6}
                md={4}
              >
                <Box
                  sx={{
                    ...styles.layoutCard,
                    ...(isSelected && styles.layoutCardSelected),
                  }}
                  onClick={() => onSelectLayout(layout._id)}
                >
                  {/* Selection Indicator */}
                  <Box sx={styles.selectionIndicator}>
                    {isSelected ? (
                      <RadioButtonCheckedIcon
                        sx={{
                          fontSize: 24,
                          color: "#4CAF50",
                        }}
                      />
                    ) : (
                      <RadioButtonUncheckedIcon
                        sx={{
                          fontSize: 24,
                          color: "#AAA",
                        }}
                      />
                    )}
                  </Box>

                  {/* Layout Image */}
                  {layout.image ? (
                    <Box sx={styles.imageContainer}>
                      <img
                        src={layout.image}
                        alt={layout.name}
                        style={styles.layoutImage}
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/300x200?text=No+Image";
                        }}
                      />
                    </Box>
                  ) : (
                    <Box sx={styles.noImageContainer}>
                      <Typography sx={styles.noImageText}>
                        No Image
                      </Typography>
                    </Box>
                  )}

                  {/* Layout Name */}
                  <Typography sx={styles.layoutName}>
                    {layout.name}
                  </Typography>

                  {/* Layout Description */}
                  {layout.description && (
                    <Typography sx={styles.layoutDescription}>
                      {layout.description}
                    </Typography>
                  )}

                  {/* Active Badge */}
                  {isSelected && (
                    <Box sx={styles.activeBadge}>
                      <Typography sx={styles.activeBadgeText}>
                        ACTIVE LAYOUT
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            );
          })
        ) : (
          <Grid container item xs={12} justifyContent="center" mt={4}>
            <Typography sx={styles.noDataText}>
              No table ordering layouts available
            </Typography>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default TableOrderingMenuLayoutTab;

const styles = {
  sectionTitle: {
    fontFamily: "GilroyBold",
    fontSize: 18,
    color: "#000",
  },
  sectionDescription: {
    fontFamily: "GilroyRegular",
    fontSize: 14,
    color: "#666",
  },
  layoutCard: {
    backgroundColor: "#fff",
    borderRadius: 2,
    padding: 2,
    position: "relative",
    transition: "all 0.3s ease",
    border: "2px solid #AAA",
    cursor: "pointer",
    height: "100%",
    "&:hover": {
      borderColor: "#666",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    },
  },
  layoutCardSelected: {
    borderColor: "#4CAF50",
    boxShadow: "0 4px 12px rgba(76, 175, 80, 0.2)",
  },
  selectionIndicator: {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 2,
  },
  imageContainer: {
    width: "100%",
    height: 200,
    backgroundColor: "#f5f5f5",
    borderRadius: 1,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mb: 2,
  },
  layoutImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  noImageContainer: {
    width: "100%",
    height: 200,
    backgroundColor: "#f5f5f5",
    borderRadius: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mb: 2,
  },
  noImageText: {
    color: "#AAA",
    fontSize: 14,
    fontFamily: "GilroyRegular",
  },
  layoutName: {
    color: "#000",
    fontSize: 16,
    fontFamily: "GilroySemiBold",
    mb: 1,
  },
  layoutDescription: {
    color: "#666",
    fontSize: 14,
    fontFamily: "GilroyRegular",
    mb: 2,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  activeBadge: {
    backgroundColor: "#4CAF50",
    borderRadius: "30px",
    py: 0.8,
    px: 2,
    display: "inline-block",
  },
  activeBadgeText: {
    color: "#fff",
    fontSize: 10,
    fontFamily: "GilroySemiBold",
    textAlign: "center",
  },
  noDataText: {
    color: "#AAA",
    fontSize: 16,
    fontFamily: "GilroyRegular",
  },
};
